/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Abilities from './Abilities';
import MonsterOpen5e from './MonsterOpen5e';
import ApiService from '../services/ApiService';
import Race from './Race';
import Class from './Class';
import Background from './Background';
import Spell from './Spell';
import SpellService from '../services/SpellService';
import BackgroundService from '../services/BackgroundService';
import ClassService from '../services/ClassService';
import RaceService from '../services/RaceService';
import FeatService from '../services/FeatService';
import Feat from './Feat';

class CachedElement<T> {
  nextUrl: string = '';
  totalCount: number | null = null;
  progress = 'Loading...';
  data: T[] = [];
  constructor(url: string) {
    this.nextUrl = url;
  }

  clear() {
    this.nextUrl = '';
    this.totalCount = 0;
    this.progress = 'Loading...';
    this.data.length = 0;
    }
}

class DataModel {

  private subscribers: (() => void)[] = [];

  documents: Map<string, string> = new Map();
  magicItems:   CachedElement<Ability>       = new CachedElement<Ability>('');
  monsters:     CachedElement<MonsterOpen5e> = new CachedElement<MonsterOpen5e>('');
  races:        CachedElement<Race>          = new CachedElement<Race>('');
  classes:      CachedElement<Class>         = new CachedElement<Class>('');
  backgrounds:  CachedElement<Background>    = new CachedElement<Background>('');
  feats:        CachedElement<Ability>       = new CachedElement<Ability>('');
  spells:       CachedElement<Spell>         = new CachedElement<Spell>('');
  spellsBySpellLevelByClass: Map<string, Map<number, Spell[]>> = new Map();

  subscribe(callback: () => void) {
    this.subscribers.push(callback);
  }

  notify() {
    this.subscribers.forEach((callback) => callback());
  }

  public fetchDocuments = async (cacheOnly: boolean) => {
    try {
      if (this.documents.size == 0) {
        const docs = await ApiService.fetchDocuments(cacheOnly);
        if (docs) {
          this.documents = docs;
        }
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  };

  public clear = () => {
    this.documents.clear();
    this.magicItems.clear();
    this.monsters.clear();
    this.races.clear();
    this.classes.clear();
    this.backgrounds.clear();
    this.feats.clear();
    this.spells.clear();
    this.spellsBySpellLevelByClass.clear();
    this.notify(); // Notify subscribers of changes
  }
  
  public fetchMagicItems = async (cacheOnly: boolean) => {
    await this.fetchDocuments(cacheOnly);
    await this.fetch((callback) => ApiService.getMagicItems(cacheOnly, callback),
                     this.magicItems, 'Magic Item', (data: Ability[], done: boolean) => { Abilities.setMagicItems(data, this.documents); });
  };
  public fetchSpells = async (cacheOnly: boolean) => {
    await this.fetchDocuments(cacheOnly);
    await this.fetch((callback) => ApiService.getAllSpells(cacheOnly, callback),
                     this.spells, 'Spell', (data: Spell[], done: boolean) => {
      this.spellsBySpellLevelByClass = SpellService.parseSpells(data);
      this.notify(); // Notify subscribers of changes
    });
  };
  private fetchMonsters   = async (cacheOnly: boolean) => {await this.fetch((callback) => ApiService.getAllMonsters(         cacheOnly, callback), this.monsters,    'Monster');};
  private fetchRaces      = async (cacheOnly: boolean) => {await this.fetch((callback) => RaceService.fetchRaces(            cacheOnly, callback), this.races,       'Race');};
  private fetchClass      = async (cacheOnly: boolean) => {await this.fetch((callback) => ClassService.fetchClasses(         cacheOnly, callback), this.classes,     'Class');};
  private fetchBackground = async (cacheOnly: boolean) => {await this.fetch((callback) => BackgroundService.fetchBackgrounds(cacheOnly, callback), this.backgrounds, 'Background');};
  private fetchFeats      = async (cacheOnly: boolean) => {await this.fetch((callback) => FeatService.fetchFeats(            cacheOnly, callback), this.feats,       'Feat');};

  private async fetch<T>(accessorFunction: (callback: (data: T[], totalCount: number) => void) => Promise<T[]>,
                         obj: CachedElement<T>,
                         name: string,
                         onUpdate?: (data: T[], done: boolean) => void
                        ): Promise<void> {
    try {
      // Call the accessor function with a callback to update `obj`
      const results = await accessorFunction((data: T[], totalCount: number) => {
        obj.totalCount = totalCount;
        obj.progress = `Loaded ${data.length} of ${totalCount} ${name}s...`;
        obj.data = data;
        if (onUpdate) {
          onUpdate(data, false);
        }
        this.notify(); // Notify subscribers of changes
      });

      if (onUpdate) {
        onUpdate(results, true);
      }

      this.notify(); // Notify subscribers of changes

      // Final update after fetch is complete
      obj.progress = '';
      obj.data = results;
    } catch (error) {
      console.error(`Failed to fetch ${name}s`, error);
    }
  }

  public fetchAll = async (cacheOnly: boolean, onMonstersLoaded?: () => void) => {
    // First, load the mater list of documents:
    await this.fetchDocuments(cacheOnly);
    // Next, load the monsters, as it drives the monster selector and grid search
    await this.fetchMonsters(cacheOnly);
    onMonstersLoaded && onMonstersLoaded();
   
    // Then load the elements needed for the NPC modal (and also the spellbook task of the guided tour):
    // Load the quickest things to load first:
    await this.fetchRaces(cacheOnly);
    await this.fetchClass(cacheOnly);
    await this.fetchBackground(cacheOnly);
    await this.fetchFeats(cacheOnly);
    // Next, load the spells:
    await this.fetchSpells(cacheOnly);

    // Finally, load the magic items used by the 'Inventory' block:
    await this.fetchMagicItems(cacheOnly);
  }
}
export default DataModel;